import React, { useState, useEffect, useMemo } from 'react';
import { Spin, Image } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { get } from 'lodash';

import { renderIntlLabel } from '@/utils';
import * as PaymentApi from '@/api/payment';
import IconFont from '@/components/IconFont';
import { parseFen2Currency } from '@/utils/number';

import xuanzhuIcon from '@/assets/img/xuanzhu.png';
import paymentOrderSrc from '@/assets/img/payment_order.png';
import bankIconSrc from '@/assets/img/offline_bank.png';

import { PAYMENT_TYPE_ICON_MAP } from './config';
import { PaymentProps } from './interface';
import styles from './index.module.scss';

const Payment: React.FC<PaymentProps> = (props) => {
  // 默认会员信息
  const defaultVipInfo = {
    accountType: 0,
    balance: 0,
    idCardNo: '',
    wyuid: '',
  };
  const { excludePayWay, isInitPayTypes, resvNo, businessType = 1 } = props;

  // const selfBusinessType = businessType || 1; // 收款业务类型, 默认1: 房间消费

  const [payTypesLoading, setPayTypesLoading] = useState(false);
  const [vipInfoLoading, setVipInfoLoading] = useState(false);
  const [payTypes, setPayTypes] = useState([]); // 全部收款方式
  const [payType, setPayType] = useState(3); // 收款方式, 默认扫码支付
  const [vipInfo, setVipInfo] = useState(defaultVipInfo); // 会员信息
  const [disabledVipPay, setDisabledVipPay] = useState(false); // 会员卡扣可点击状态
  const [certNo, setCertNo] = useState(''); // 证件号
  const [wyuid, setWyuid] = useState('');
  const [xuanzhuBalance, setXuanzhuBalance] = useState(null); // 选住余额

  // 获取收款方式
  const initPayTypes = async () => {
    setPayTypesLoading(true);
    try {
      const res = await PaymentApi.fetchPayTypes({
        businessType,
      });
      if (res && res.status) {
        let types = get(res, 'data.paymentTypeList', []);
        let tmpTypes = [];
        // const tmpObj = {}; // 临时对象, 去重用
        if (types.length > 0) {
          types.sort((a, b) => a?.cashierOrder - b?.cashierOrder);
          // types.forEach((item) => {
          //   // 根据payWay去重, 判断启用状态(0: 启用, 1: 未启用)
          //   if (!tmpObj[item.payWay] && item.status === 0) {
          //     tmpTypes.push({
          //       ...item,
          //       icon: PAYMENT_TYPE_ICON_MAP[item.payWay],
          //       paymentTypeName: item.payWay === 3 ? '扫码支付' : item.paymentTypeName,
          //     });
          //   }
          //   tmpObj[item.payWay] = item.paymentTypeName;
          // });
          types = types?.map((item) => ({
            ...item,
            icon: PAYMENT_TYPE_ICON_MAP[item.payWay],
            paymentTypeName:
              item.payWay === 3 ? renderIntlLabel('bill_enum_payWay_scan') : item.paymentTypeName,
          }));
          const scanItem = types?.find((item) => item.payWay === 3 && item.status === 0);
          const remainTypeList = types?.filter((item) => item.payWay !== 3 && item.status === 0);
          if (scanItem) {
            tmpTypes.push(scanItem);
          }
          if (Array.isArray(remainTypeList) && remainTypeList.length) {
            tmpTypes = tmpTypes.concat(remainTypeList);
          }
        }
        // 过滤付款单
        tmpTypes = tmpTypes?.filter((item) => !excludePayWay?.includes(item.payWay));
        setPayTypes(tmpTypes);
        if (tmpTypes.length > 0) {
          setPayType(tmpTypes[0].payWay);
          props.selectPayType(tmpTypes[0].payWay);
        }
      } else {
        setPayTypes([]);
      }
      setPayTypesLoading(false);
    } catch (err) {
      setPayTypes([]);
      setPayTypesLoading(false);
    }
  };

  // 获取房主会员信息
  const initVipInfo = async (no) => {
    setVipInfoLoading(true);
    try {
      const { status, data } = await PaymentApi.fetchVipInfoByOrderNo({
        resvNo: no,
      });
      if (status) {
        if (data && data.memberLevel !== null && data.memberLevel !== '') {
          setVipInfo({
            accountType: data.accountType,
            balance: data.balance,
            idCardNo: data.certNo,
            wyuid: data.wyuid,
          });
        } else {
          setVipInfo(defaultVipInfo);
          setDisabledVipPay(true);
        }

        setCertNo(data?.certNo);
        setWyuid(data?.wyuid);
        setXuanzhuBalance(data?.xzBalance);

        setVipInfoLoading(false);
      }
    } catch (err) {
      setVipInfoLoading(false);
      setVipInfo(defaultVipInfo);
    }
  };

  // 收款方式变更
  const changePayType = (type) => {
    if (type === 2 && disabledVipPay) return false;

    setPayType(type);
    props.selectPayType(type);

    if (resvNo) {
      if (type === 2 || type === 17) {
        if (props.setIdCardNo) {
          props.setIdCardNo(certNo);
        }
        if (props.setWyuid) {
          props.setWyuid(wyuid);
        }
      }
    }

    return false;
  };

  // init
  useMemo(() => {
    if (isInitPayTypes) {
      initPayTypes();
      // init vip info
      if (resvNo) initVipInfo(resvNo);
    }
  }, [isInitPayTypes, businessType]);

  // reset state when unmount
  useEffect(() => {
    return () => {
      setPayTypes([]);
      setPayType(3);
      setVipInfo(defaultVipInfo);
      setDisabledVipPay(false);
      setCertNo('');
      setWyuid('');
      setXuanzhuBalance(null);
    };
  }, []);

  const renderPayRemark = (data) => {
    if (data.payWay !== 2 && data.payWay !== 17) return data.remark;

    if (data.payWay === 2) {
      if (resvNo && vipInfo.idCardNo !== '') {
        return (
          <>
            {renderIntlLabel('bill_label_remain_money')}
            <span>{parseFen2Currency(Math.abs(vipInfo.balance))}</span>
          </>
        );
      }

      if (!resvNo) {
        return renderIntlLabel('bill_msg_input_certno');
      }

      if (resvNo && vipInfo.idCardNo === '') {
        return renderIntlLabel('bill_label_not_member');
      }
    }

    if (data.payWay === 17) {
      return (
        <>
          {renderIntlLabel('bill_label_remain_money')}
          <span>{parseFen2Currency(Math.abs(xuanzhuBalance))}</span>
        </>
      );
    }

    return null;
  };

  const renderPayTypes = () => {
    if (payTypes.length === 0) return '';

    return payTypes.map((type) => {
      if (!type.payWay) return null;

      // 选住余额特殊处理
      if (type.payWay === 17 && !xuanzhuBalance) {
        return null;
      }

      return (
        <li
          key={type.id}
          className={[
            payType === type.payWay ? styles.choosed : '',
            type.payWay === 2 && disabledVipPay ? styles.disabled : '',
          ].join(' ')}
          onClick={() => changePayType(type.payWay)}
        >
          <div className={styles['pay-type-content']}>
            {type.payWay === 9 ? (
              <Image src={paymentOrderSrc} preview={false} width={24} height={24} />
            ) : null}
            {type.payWay === 17 ? (
              <Image src={xuanzhuIcon} preview={false} width={24} height={24} />
            ) : null}
            {type.payWay === 18 ? (
              <Image src={bankIconSrc} preview={false} width={32} height={22} />
            ) : null}
            {type.payWay !== 9 && type.payWay !== 17 && type.payWay !== 18 ? (
              <i
                className={
                  type.payWay === 2 && disabledVipPay ? styles['disabled-vip'] : styles[type.icon]
                }
              >
                <IconFont type={type.icon} />
              </i>
            ) : null}
            <div className={styles['pay-type-text']}>
              <p>{type.paymentTypeName}</p>
              <p className={styles['pay-type-des']}>{renderPayRemark(type)}</p>
            </div>
          </div>
          {payType === type.payWay ? (
            <i className={styles['choosed-icon']}>
              <CheckOutlined />
            </i>
          ) : (
            ''
          )}
        </li>
      );
    });
  };

  return (
    <Spin spinning={payTypesLoading && vipInfoLoading}>
      <div className={styles['pay-types-wrapper']}>
        <label className="text-colon">{renderIntlLabel('bill_title_receive_money_ways')}</label>
        <ul className={styles['pay-types']}>{renderPayTypes()}</ul>
      </div>
    </Spin>
  );
};

export default React.memo(Payment);
